/**
 * Labelling helpers for the blind ground-truth form's reveal panel (GroundTruthForm).
 *
 * The arithmetic is NOT here. `api/truth.py` classifies every observation
 * (`match | false_positive | miss | uncertain | no_verdict | excluded`) and computes the
 * counts, and that is also what the server-side CSV uses. This file used to recompute a
 * second, differently named taxonomy from the raw rows — two implementations of the one
 * number the project is judged on, guaranteed to diverge. The export lives on the server
 * (`GET /api/truth/agreement.csv`, `GET /api/agreement`) — a second column list here was
 * the same divergence in a different place.
 *
 * The Сверка screen was removed on 2026-09-05 (решение владельца: методология ушла из
 * продукта в docs), and its hectares/empty-trips presentation helpers went with it. What
 * is left serves the one surviving consumer: the reveal panel of the blind form, behind
 * VERIFICATION_MODE.
 *
 * "Освободилось" means the standing crop is gone: stubble, bare soil and a sown field all
 * count as free, standing crop does not, `not_reached` is excluded — the server's rule,
 * mirrored here only for the reveal panel of the blind form.
 */

import { T } from "../strings";
import type { GroundTruthState, Outcome } from "../api/types";

const FREE_STATES: GroundTruthState[] = ["stubble", "bare_soil", "sown"];

export const observedIsFree = (state: GroundTruthState): boolean => FREE_STATES.includes(state);

export const OUTCOME_COLOR_VAR: Record<Outcome, string> = {
  match: "var(--state-veg)",
  false_positive: "var(--state-reject)",
  miss: "var(--state-bare)",
  uncertain: "var(--state-maybe)",
  no_verdict: "var(--state-nodata)",
  excluded: "var(--rule-strong)",
};

// Имена исходов живут в `groundTruth.outcomes` с 2026-09-05: секция `reconcile` удалена из
// словаря целиком вместе с экраном сверки, тексты переехали без изменений.
export const OUTCOME_LABEL: Record<Outcome, string> = {
  match: T.groundTruth.outcomes.match,
  false_positive: T.groundTruth.outcomes.false_positive,
  miss: T.groundTruth.outcomes.miss,
  uncertain: T.groundTruth.outcomes.uncertain,
  no_verdict: T.groundTruth.outcomes.no_verdict,
  excluded: T.groundTruth.outcomes.excluded,
};

/** Server rule (truth.classify_outcome), for the one place with no server row yet: the
 *  reveal panel right after a blind entry, where the verdict is on the client already. */
export const outcomeOfPair = (
  observed: GroundTruthState,
  decisionIsFree: boolean | null,
): Outcome => {
  if (observed === "not_reached") return "excluded";
  if (decisionIsFree === null) return "no_verdict";
  return decisionIsFree === observedIsFree(observed) ? "match" : "false_positive";
};
